// Brand page inventory: CaboPOC is served by this route file (`_public.cabopoc.tsx`).
import type { LinksFunction, MetaFunction } from "react-router";
import CaboPocLogo from "~/assets/logos/new_logos/CaboPoc_Claim@4x.png";
import ContactBannerImage from "~/assets/images/cabopoc_contact_banner.jpeg";
import IglooProDetailed from "~/assets/images/igloo_pro_1.png";
import IglooProClean from "~/assets/images/igloo_pro_1_v2.png";
import { ContactBanner } from "~/features/microsite/ContactBanner";
import { PublicFooter } from "~/features/public/Footer";
import { NavBar } from "~/features/public/NavBar";

export const meta: MetaFunction = () => {
  return [
    { title: "CaboPOC – Point-of-Care-Diagnostik mit Igloo Pro Multireader" },
    {
      name: "description",
      content:
        "CaboPOC bringt professionelle POC-Diagnostik in Ihre Apotheke. Igloo Pro Multireader mit 5 Analysemethoden, digitale Terminbuchung und vollständiger Service.",
    },
    {
      name: "keywords",
      content:
        "CaboPOC, Point-of-Care, Diagnostik, Apotheke, Igloo Pro, Multireader, Immunfluoreszenz, Schnelltest, Hamburg",
    },
    { name: "author", content: "HEALYZER – Health Analyzing Technologies GmbH" },
    { name: "robots", content: "index, follow" },
    { httpEquiv: "Content-Language", content: "de" },

    // Open Graph / Facebook
    { property: "og:type", content: "website" },
    { property: "og:title", content: "CaboPOC – Point-of-Care-Diagnostik mit Igloo Pro Multireader" },
    {
      property: "og:description",
      content:
        "CaboPOC bringt professionelle POC-Diagnostik in Ihre Apotheke. Igloo Pro Multireader mit 5 Analysemethoden, digitale Terminbuchung und vollständiger Service.",
    },
    { property: "og:url", content: "https://www.cabocheck.com/cabopoc" },
    { property: "og:site_name", content: "CaboPOC" },
    { property: "og:locale", content: "de_DE" },

    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "CaboPOC – Point-of-Care-Diagnostik mit Igloo Pro Multireader" },
    {
      name: "twitter:description",
      content:
        "CaboPOC bringt professionelle POC-Diagnostik in Ihre Apotheke. Igloo Pro Multireader mit 5 Analysemethoden, digitale Terminbuchung und vollständiger Service.",
    },

    // Additional SEO
    { name: "theme-color", content: "#4f46e5" },
    { name: "format-detection", content: "telephone=no" },
    { rel: "canonical", href: "https://www.cabocheck.com/cabopoc" },
  ];
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap",
  },
  { rel: "icon", href: "/cabopoc-apple-touch-icon.png", type: "image/png" },
  { rel: "apple-touch-icon", href: "/cabopoc-apple-touch-icon.png", sizes: "180x180" },
  { rel: "shortcut icon", href: "/cabopoc-favicon.ico" },
];

const COMPANY_INFO = {
  name: "CaboPOC",
  fullName: "HEALYZER – Health Analyzing Technologies GmbH",
  address: {
    street: "Zimmerpforte 3",
    postal: "20099",
    city: "Hamburg",
    country: "DE",
  },
  email: "hilfe@cabocheck.de",
  url: "https://www.cabocheck.com/cabopoc",
};

const navigationItems = [
  { href: "#benefits", label: "Vorteile" },
  { href: "#features", label: "Gerät & Tests" },
  { href: "#digital", label: "Digital" },
  { href: "#wirtschaftlich", label: "Wirtschaftlich" },
  { href: "#contact", label: "Kontakt aufnehmen" },
];

export default function CaboPOC() {
  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const element = document.getElementById(targetId.replace("#", ""));
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const CompanyLogo = ({ className = "h-28" }: { className?: string }) => (
    <div className="flex items-center gap-2">
      <img src={CaboPocLogo} alt="CaboPOC Logo" className={`${className} select-none`} />
    </div>
  );

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: COMPANY_INFO.name,
    description:
      "Point-of-Care-Diagnostik-System mit Igloo Pro Multireader für Apotheken – Terminbuchung, Ergebnisverwaltung und digitaler Service aus einer Hand.",
    url: COMPANY_INFO.url,
    logo: `${COMPANY_INFO.url}-logo.png`,
    foundingLocation: {
      "@type": "Place",
      name: COMPANY_INFO.address.city,
      addressCountry: COMPANY_INFO.address.country,
    },
    parentOrganization: {
      "@type": "Organization",
      name: COMPANY_INFO.fullName,
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY_INFO.address.street,
      postalCode: COMPANY_INFO.address.postal,
      addressLocality: COMPANY_INFO.address.city,
      addressCountry: COMPANY_INFO.address.country,
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: COMPANY_INFO.email,
      contactType: "customer service",
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />

      <div className="scroll-smooth bg-slate-50 font-inter text-slate-900" lang="de">
        <div className="relative bg-public-radial">
          <div className="absolute inset-x-0 -top-20 -z-10 h-52 bg-gradient-to-b from-indigo-200/40 to-transparent"></div>
          <NavBar
            logo={<CompanyLogo className="h-20" />}
            logoTo="/"
            navItems={[
              { to: "/", label: "← Startseite", id: "home" },
              ...navigationItems.map((item) => ({
                to: `/cabopoc${item.href}`,
                label: item.label,
                id: item.href.replace("#", ""),
              })),
            ]}
          />
          <section className="public-container pb-14 pt-4">
            <div className="grid gap-10 md:grid-cols-2 md:items-start">
              <div>
                <h1 className="mt-10 text-4xl font-extrabold tracking-tight sm:text-5xl">
                  Diagnostik vor Ort – kombiniert mit{" "}
                  <span className="text-blue-550">CaboChecks digitalem Rundum-Service</span>
                </h1>
                <p className="mt-6 max-w-xl text-lg text-slate-700">
                  CaboPoc verbindet modernste Schnelltest-Technologie mit Terminbuchung, Ergebnisverwaltung und
                  automatisierter Nachbestellung – alles aus einer Hand.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="#features"
                    onClick={(e) => handleSmoothScroll(e, "#features")}
                    className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold hover:border-slate-400"
                  >
                    Mehr erfahren
                  </a>
                </div>
              </div>

              {/* Hero Image: Igloo Pro with blood drop */}
              <div className="relative isolate pl-20">
                <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-indigo-100 to-blue-100"></div>
                <img
                  src={IglooProDetailed}
                  alt="Igloo Pro Multireader mit Vitamin D Display und Blutprobe"
                  className="h-auto w-full rounded-2xl drop-shadow-lg"
                />
              </div>
            </div>
          </section>
        </div>

        {/* Trust / Logos */}
        <section className="public-container pb-6">
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4 text-slate-500">
            <span className="text-xs uppercase tracking-wider">ISO 13485 zertifiziert</span>
            <span aria-hidden="true" className="h-4 w-px bg-slate-300"></span>
            <span className="text-xs uppercase tracking-wider">CE-konform</span>
            <span aria-hidden="true" className="h-4 w-px bg-slate-300"></span>
            <span className="text-xs uppercase tracking-wider">Made in Hamburg</span>
          </div>
        </section>

        {/* System Solution Section */}
        <section id="system" className="public-container py-12">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold">CaboPoc als Systemlösung</h2>
            <p className="mt-2 text-lg text-slate-700">Mehr als ein Gerät: CaboPoc ist Ihr komplettes POC-Ökosystem</p>
            <p className="mt-4 text-slate-600">
              Von Hardware über Software bis Schulung, Hotline und Qualitätskontrolle – CaboPoc liefert ein vollständig
              betreutes Diagnostiksystem für Apotheken.
            </p>
          </div>
        </section>

        {/* Benefits Overview */}
        <section id="benefits" className="public-container py-12">
          <div className="grid gap-8 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold">Alles in einer Lösung</h3>
              <p className="mt-2 text-sm text-slate-600">
                Mehr als ein Gerät: CaboPoc ist Ihr komplettes POC-Ökosystem – von Hardware über Software bis Schulung,
                Hotline und Qualitätskontrolle.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold">Wirtschaftlich & risikoarm</h3>
              <p className="mt-2 text-sm text-slate-600">
                CaboCheck übernimmt die Gerätefinanzierung – Sie starten mit geringem Anfangsinvest und planbaren
                Verbrauchskosten.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold">Schneller Start</h3>
              <p className="mt-2 text-sm text-slate-600">
                Plug & Play: Nach kurzer digitaler Schulung kann Ihr Team direkt starten – kein Umbau, keine
                komplizierte Technik.
              </p>
            </div>
          </div>
        </section>

        {/* POC Diagnostics Intro */}
        <section id="poc-intro" className="public-container py-12">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold">Point-of-Care-Diagnostik. Schnell testen. Digital verwalten.</h2>
            <p className="mt-4 text-lg text-slate-700">
              CaboPoc bringt professionelle Vor-Ort-Diagnostik direkt in Ihre Apotheke – mit dem einzigartigen offenen
              Point-of-Care-System, kombiniert mit CaboChecks digitaler Infrastruktur. Fünf Analysemethoden in einem
              kompakten Gerät, mit unterschiedlichsten Testoptionen, vollständig integrierte Prozesse: Von der
              Terminbuchung bis zur Ergebnisverwaltung – alles aus einer Hand.
            </p>
          </div>
        </section>

        {/* Device Features Section */}
        <section id="features" className="public-container py-12">
          <div className="grid items-start gap-12 md:grid-cols-2">
            <div>
              <div className="mb-8 mt-4 max-w-2xl">
                <h2 className="text-2xl font-bold">Ein Gerät. Alle Testarten.</h2>
              </div>
              <p className="text-lg text-slate-700">
                Im Zentrum von CaboPoc steht der Igloo Pro Multireader – das einzige vollständig offene
                Point-of-Care-Diagnosesystem.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex gap-3">
                  <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                  <p className="text-slate-700">Immunfluoreszenz</p>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                  <p className="text-slate-700">Kolorimetrie</p>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                  <p className="text-slate-700">Trockenchemie</p>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                  <p className="text-slate-700">Mikrofluidik</p>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                  <p className="text-slate-700">Mikrodosierung</p>
                </li>
              </ul>
              <p className="mt-6 text-slate-600">
                Dank offener Architektur sind über 90 % aller marktüblichen Schnelltests kompatibel – maximale Freiheit
                bei der Testauswahl und volle Versorgungssicherheit. Aus Qualitätssicherungsgründen stellen wir Ihnen
                ausschließlich Tests zur Verfügung, welche zuvor validiert wurden.
              </p>
            </div>

            {/* Device Image: Clean product shot */}
            <div className="relative isolate">
              <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-blue-100 to-indigo-100"></div>
              <img
                src={IglooProClean}
                alt="Igloo Pro Multireader Gerät – Produktansicht"
                className="h-auto w-full rounded-2xl drop-shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* Test Parameters Section */}
        <section id="tests" className="public-container py-12">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold">Relevante Tests für den Apothekenalltag</h2>
            <p className="mt-2 text-slate-700">Mit CaboPoc können Sie u. a. folgende Parameter anbieten:</p>
            <ul className="mt-6 grid gap-3 md:grid-cols-2">
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Vitamin D</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Ferritin</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>HbA1c</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>CRP</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>TSH</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Lipidpanel (Cholesterin, HDL, LDL, Triglyceride, Glukose)</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Testosteron (in Kürze verfügbar)</p>
              </li>
            </ul>
            <p className="mt-6 text-sm text-slate-600">
              Als Probenmaterial genügt Kapillarblut – einfach, schnell und kundenfreundlich.
            </p>
          </div>
        </section>

        {/* Integration Section */}
        <section id="integration" className="public-container py-12">
          <div className="max-w-3xl">
            <h2 className="text-2xl font-bold">Einfach integriert – Plug & Play ab Tag 1</h2>
            <p className="mt-2 text-lg text-slate-700">
              CaboPoc ist sofort einsatzbereit. Kein Umbau, keine komplizierte Technik:
            </p>
            <ul className="mt-6 space-y-4">
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Geringer Platzbedarf</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Tests in wenigen Minuten durchgeführt</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Integrierter Timer</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">
                  Ergebnisse direkt am Display, per QR-Code oder in unserem CaboPoc Portal
                </p>
              </li>
            </ul>
            <p className="mt-6 text-slate-700">Nach kurzer digitaler Schulung kann Ihr Team direkt starten.</p>
          </div>
        </section>

        {/* Digital Infrastructure Section */}
        <section id="digital" className="public-container py-12">
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-8 shadow-sm">
            <h2 className="text-2xl font-bold">Digital gedacht – mit CaboCheck Infrastruktur</h2>
            <p className="mt-2 text-lg text-slate-700">
              CaboPoc ist mehr als ein Diagnostikgerät. Sie erhalten ein vollständiges digitales Servicepaket:
            </p>
            <ul className="mt-6 space-y-4">
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Softwareanbindung an das CaboCheck-Portal</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Integriertes Terminbuchungssystem</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Personalisierte Microsite für Ihre Apotheke</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Automatisierte Nachbestellungen</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Schulungsmaterial</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Hotline</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Zugriff auf alle relevanten Verordnungen und Zertifikate</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p className="text-slate-700">Qualitätssicherung durch Kontrolltestungen</p>
              </li>
            </ul>
            <p className="mt-6 font-semibold text-slate-700">
              Alles zentral organisiert – für maximale Effizienz im Alltag.
            </p>
          </div>
        </section>

        {/* Economic Benefits Section */}
        <section id="wirtschaftlich" className="public-container py-12">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold">Wirtschaftlich attraktiv & risikoarm</h2>
            <p className="mt-2 text-lg text-slate-700">
              CaboCheck übernimmt die Gerätefinanzierung – Sie starten mit geringem Anfangsinvest.
            </p>
            <div className="mt-8 grid gap-8 md:grid-cols-2">
              <div>
                <h3 className="text-lg font-semibold">Ihre Vorteile</h3>
                <ul className="mt-4 space-y-3">
                  <li className="flex gap-3">
                    <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                    <p className="text-slate-700">Einzelbestellung von Testkits → keine hohen Lagerbestände</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                    <p className="text-slate-700">Planbare Verbrauchskosten</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                    <p className="text-slate-700">Digitale Prozesse</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                    <p className="text-slate-700">Neue, margenstarke Dienstleistungen</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                    <p className="text-slate-700">
                      Cross-Selling-Potenzial im Beratungsgespräch auch für andere Testungen mittels CaboLab
                    </p>
                  </li>
                </ul>
              </div>
              <div className="flex items-center">
                <p className="text-slate-700">
                  Nach der ersten Vertragslaufzeit geht das Gerät in Ihren Besitz über – danach zahlen Sie nur noch die
                  monatliche Servicepauschale.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Full-Service Package Section */}
        <section id="service" className="public-container py-12">
          <div className="max-w-4xl">
            <h2 className="text-2xl font-bold">Rundum-Sorglos mit CaboPoc</h2>
            <p className="mt-2 text-lg text-slate-700">Im CaboPoc-Paket enthalten:</p>
            <ul className="mt-6 grid gap-3 md:grid-cols-2">
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Igloo Pro Gerät inkl. Adapter</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Testkits flexibel bestellbar</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Software & Portalzugang</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Terminbuchung</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Microsite</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Automatisierte Nachbestellung</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Schulungen</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Hotline</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Qualitätskontrolle</p>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-blue-550"></span>
                <p>Dokumentationsunterstützung</p>
              </li>
            </ul>
            <p className="mt-6 text-lg font-semibold text-slate-800">Ein echtes Full-Service-Modell für Apotheken</p>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how" className="public-container py-12">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold">So funktioniert&apos;s in der Praxis</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 p-6">
                <div className="text-sm font-semibold text-blue-550">1 · Bestellung & Setup</div>
                <p className="mt-2 text-sm text-slate-700">
                  Gerät geliefert, digitale Schulung absolviert, keine Installation nötig – Sie sind startklar.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-6">
                <div className="text-sm font-semibold text-blue-550">2 · Testen & Verwalten</div>
                <p className="mt-2 text-sm text-slate-700">
                  Tests durchführen, Ergebnisse digital im Portal verwalten, Patienten informieren.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-6">
                <div className="text-sm font-semibold text-blue-550">3 · Nachbestellung & Support</div>
                <p className="mt-2 text-sm text-slate-700">
                  Automatische Nachbestellung der Testkits, Hotline für Fragen, kontinuierliche Qualitätskontrolle.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="public-container py-12">
          <ContactBanner
            eyebrow="CaboPOC kennenlernen"
            title="Kontaktieren Sie uns noch heute"
            description="Registrieren Sie sich und wir melden uns bei Ihnen, um CaboPOC, passende Tests und den Start in Ihrer Apotheke gemeinsam zu planen."
            to="/register/pharmacy/step2"
            buttonLabel="Kontakt aufnehmen"
            imageSrc={ContactBannerImage}
            imageAlt="Apothekenteam im Beratungsgespräch"
          />
        </section>

        <PublicFooter />
      </div>
    </>
  );
}
